import React, { FC, useCallback, useId, useRef, useState } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvButton,
	EvCaptionDefault,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	EvSection,
	EvTextInput,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_ADD_VALUE_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_ADD_VALUE_INPUT_DAID,
	LABEL_MANAGEMENT_PANEL_CSV_ERROR_BANNER_DAID,
	LABEL_MANAGEMENT_PANEL_CSV_ERROR_DOWNLOAD_DAID,
	LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_INPUT_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';
import { Logger } from 'src/common/services/Logger';

import { LABEL_PANEL_VALIDATION } from '../../LabelPanel.utils';

import { downloadLabelValuesCsvErrorReport } from './LabelValuesCsvReport';
import { isAcceptableCsvUploadFile, MAX_CSV_FILE_SIZE_BYTES } from './LabelValuesSection.constants';
import { LABEL_VALUES_LABELS } from './LabelValuesSection.labels';
import { LabelValuesSectionProps } from './LabelValuesSection.types';
import { ValuesTable } from './values-table';

export const LabelValuesSection: FC<LabelValuesSectionProps> = ({
	values,
	showDashboardVisibility = false,
	onAddValue,
	onRemoveValue,
	onToggleDashboardVisibility,
	onUploadCsvContent,
	onFormTouched,
	csvUploadError,
	csvRejectedFileValues
}) => {
	const [newValue, setNewValue] = useState('');
	const [addValueError, setAddValueError] = useState<string | undefined>();
	const inputLabelId = useId();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [localUploadError, setLocalUploadError] = useState<string | undefined>();

	const handleUploadValuesClick = useCallback(() => {
		onFormTouched?.();
		fileInputRef.current?.click();
	}, [onFormTouched]);

	const handleCsvFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			event.target.value = '';
			if (!file) {
				return;
			}

			onFormTouched?.();

			if (!isAcceptableCsvUploadFile(file)) {
				setLocalUploadError(LABEL_VALUES_LABELS.UPLOAD_INVALID_FILE_TYPE);
				return;
			}

			if (file.size > MAX_CSV_FILE_SIZE_BYTES) {
				setLocalUploadError(LABEL_VALUES_LABELS.UPLOAD_FILE_TOO_LARGE);
				return;
			}

			const reader = new FileReader();
			reader.onload = () => {
				setLocalUploadError(undefined);
				const text = typeof reader.result === 'string' ? reader.result : '';
				onUploadCsvContent(text);
			};
			reader.onerror = () => {
				Logger.error('LabelValuesSection: failed to read CSV file', { name: file.name });
				setLocalUploadError(LABEL_VALUES_LABELS.UPLOAD_FILE_READ_ERROR);
			};
			reader.readAsText(file);
		},
		[onUploadCsvContent, onFormTouched]
	);

	const handleAddValue = useCallback(() => {
		setNewValue((currentValue) => {
			const error = onAddValue(currentValue);
			setAddValueError(error);
			return error ? currentValue : '';
		});
	}, [onAddValue]);

	const handleInputChange = useCallback(
		(value: string) => {
			onFormTouched?.();
			setNewValue(value);
			setAddValueError(undefined);
		},
		[onFormTouched]
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				handleAddValue();
			}
		},
		[handleAddValue]
	);

	const handleDownloadCsvErrorReport = useCallback(() => {
		if (csvRejectedFileValues?.length) {
			downloadLabelValuesCsvErrorReport(csvRejectedFileValues);
		}
	}, [csvRejectedFileValues]);

	return (
		<EvFlexColumn gap="LARGE">
			<EvFlexColumn gap="MEDIUM">
				<EvHeadingSmall as="h4" color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.ADD_SUBSECTION_TITLE}
				</EvHeadingSmall>
				<EvBodyMedium color={TEXT_COLOR.CONTENT_3}>
					{LABEL_VALUES_LABELS.ADD_SUBSECTION_SUBTITLE}
				</EvBodyMedium>
				<EvBodyMedium as="label" id={inputLabelId} color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.ADD_VALUE_LABEL}
				</EvBodyMedium>
				<EvFlexRow gap="MEDIUM" align="START" wrap="WRAP">
					<EvFlexColumn flex="1">
						<EvTextInput
							ariaLabelledBy={inputLabelId}
							value={newValue}
							onChange={handleInputChange}
							placeholder={LABEL_VALUES_LABELS.ADD_VALUE_PLACEHOLDER}
							maxLength={String(LABEL_PANEL_VALIDATION.VALUE_MAX_LENGTH)}
							dataAutomationId={LABEL_MANAGEMENT_PANEL_ADD_VALUE_INPUT_DAID}
							onKeyDown={handleKeyDown}
							error={addValueError}
						/>
					</EvFlexColumn>
					<EvButton
						type={BUTTON_TYPES.GHOST}
						title={LABEL_VALUES_LABELS.ADD_VALUE_BUTTON}
						onClick={handleAddValue}
						dataAutomationId={LABEL_MANAGEMENT_PANEL_ADD_VALUE_BUTTON_DAID}
					>
						{LABEL_VALUES_LABELS.ADD_VALUE_BUTTON}
					</EvButton>
					<input
						ref={fileInputRef}
						className="sr-only"
						type="file"
						accept=".csv,text/csv"
						tabIndex={-1}
						aria-label={LABEL_VALUES_LABELS.UPLOAD_VALUES_INPUT_ARIA}
						data-automation-id={LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_INPUT_DAID}
						onChange={handleCsvFileChange}
					/>
					<EvButton
						type={BUTTON_TYPES.SECONDARY}
						title={LABEL_VALUES_LABELS.UPLOAD_VALUES_BUTTON}
						onClick={handleUploadValuesClick}
						dataAutomationId={LABEL_MANAGEMENT_PANEL_UPLOAD_VALUES_BUTTON_DAID}
					>
						{LABEL_VALUES_LABELS.UPLOAD_VALUES_BUTTON}
					</EvButton>
				</EvFlexRow>
				{csvRejectedFileValues && csvRejectedFileValues.length > 0 && (
					<EvSection
						ariaLabel={LABEL_VALUES_LABELS.CSV_ERROR_BANNER_ARIA}
						ariaLive="assertive"
						dataAutomationId={LABEL_MANAGEMENT_PANEL_CSV_ERROR_BANNER_DAID}
					>
						<EvFlexColumn gap="MEDIUM" padding="MEDIUM">
							<EvBodyMedium color={TEXT_COLOR.CONTENT_2}>
								{LABEL_VALUES_LABELS.UPLOAD_INVALID_CSV_BANNER}
							</EvBodyMedium>
							<EvFlexRow>
								<EvButton
									type={BUTTON_TYPES.SECONDARY}
									title={LABEL_VALUES_LABELS.UPLOAD_INVALID_CSV_DOWNLOAD}
									onClick={handleDownloadCsvErrorReport}
									dataAutomationId={LABEL_MANAGEMENT_PANEL_CSV_ERROR_DOWNLOAD_DAID}
								>
									{LABEL_VALUES_LABELS.UPLOAD_INVALID_CSV_DOWNLOAD}
								</EvButton>
							</EvFlexRow>
						</EvFlexColumn>
					</EvSection>
				)}
				{(csvUploadError || localUploadError) && (
					<EvSection ariaLive="assertive">
						<EvCaptionDefault color={TEXT_COLOR.RED}>
							{localUploadError ?? csvUploadError}
						</EvCaptionDefault>
					</EvSection>
				)}
			</EvFlexColumn>

			{values.length > 0 && (
				<EvFlexColumn gap="MEDIUM">
					<EvHeadingSmall as="h4" color={TEXT_COLOR.CONTENT_2}>
						{LABEL_VALUES_LABELS.EXISTING_SUBSECTION_TITLE}
					</EvHeadingSmall>
					<ValuesTable
						values={values}
						showDashboardVisibility={showDashboardVisibility}
						onRemoveValue={onRemoveValue}
						onToggleDashboardVisibility={onToggleDashboardVisibility}
					/>
				</EvFlexColumn>
			)}
		</EvFlexColumn>
	);
};
